import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

export const FilterContext = React.createContext(null);

export function FilterContextProvider({ children }) {
    const [context, setContext] = React.useState({});
    const history = useHistory();
    const currentPath = React.useRef(history.location.pathname);
    const isFirstNavigationAfterMount = React.useRef(true);

    const removeFromContext = React.useCallback(names => {
        if (!Array.isArray(names)) {
            names = [names];
        }
        setContext(context => {
            let newContext = { ...context };
            names.forEach(name => {
                delete newContext[name];
            });
            return newContext;
        });
    }, []);

    React.useEffect(() => {
        const unlisten = history.listen(location => {
            if (isFirstNavigationAfterMount.current) {
                isFirstNavigationAfterMount.current = false;
                currentPath.current = location.pathname;
                return;
            }

            if (location.pathname !== currentPath.current) {
                currentPath.current = location.pathname;
                setContext({});
            }
        });

        return unlisten;
    }, [history]);

    return (
        <FilterContext.Provider
            value={{
                context,
                setContext,
                removeFromContext
            }}
        >
            {children}
        </FilterContext.Provider>
    );
}

FilterContextProvider.propTypes = {
    children: PropTypes.node
};

export function useFilterContext() {
    const ctx = React.useContext(FilterContext);
    if (!ctx) {
        throw new Error('[useFilterContext] Did you use useFilterContext outside a FilterContextProvider?');
    }
    const { context, setContext, removeFromContext } = ctx;

    return [context, setContext, removeFromContext];
}
